import { useState, useRef } from "react";
import { Upload, X, Image, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { toast } from "sonner";
import { uploadFile } from "@/lib/api";

export default function ImageUpload({ 
  value, 
  onChange, 
  label = "Image",
  className = "" 
}) {
  const [uploading, setUploading] = useState(false);
  const [mode, setMode] = useState(value?.startsWith("/api/uploads") ? "upload" : "url");
  const fileInputRef = useRef(null);

  const handleFileSelect = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Validate
    if (!file.type.startsWith("image/")) {
      toast.error("Selecteer een afbeeldingsbestand");
      return;
    }
    if (file.size > 5 * 1024 * 1024) {
      toast.error("Image must be less than 5MB");
      return;
    }

    setUploading(true);
    try {
      const response = await uploadFile(file);
      onChange(response.data.url);
      toast.success("Image uploaded successfully");
    } catch (error) {
      console.error("Upload error:", error);
      toast.error("Failed to upload image");
    } finally {
      setUploading(false);
    }
  };

  const handleRemove = () => {
    onChange("");
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  };

  return (
    <div className={`space-y-3 ${className}`}>
      <Label className="text-slate-700 dark:text-slate-300">{label}</Label>
      
      <Tabs value={mode} onValueChange={setMode} className="w-full">
        <TabsList className="grid w-full grid-cols-2 mb-3">
          <TabsTrigger value="upload">Upload Image</TabsTrigger>
          <TabsTrigger value="url">Image URL</TabsTrigger>
        </TabsList>
        
        <TabsContent value="upload" className="mt-0">
          <div className="space-y-3">
            <input
              ref={fileInputRef}
              type="file"
              accept="image/*"
              onChange={handleFileSelect}
              className="hidden"
              data-testid="image-file-input"
            />
            
            {value && (
              <div className="relative inline-block">
                <img 
                  src={value.startsWith("/api") ? `${process.env.REACT_APP_BACKEND_URL}${value}` : value}
                  alt="Preview"
                  className="h-32 w-32 object-cover rounded-lg border border-slate-200 dark:border-slate-700"
                />
                <Button
                  type="button"
                  size="icon"
                  variant="destructive"
                  className="absolute -top-2 -right-2 h-6 w-6"
                  onClick={handleRemove}
                >
                  <X className="h-4 w-4" />
                </Button>
              </div>
            )}
            
            <Button
              type="button"
              variant="outline"
              onClick={() => fileInputRef.current?.click()}
              disabled={uploading}
              className="w-full border-dashed"
              data-testid="upload-button"
            >
              {uploading ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Uploading...
                </>
              ) : (
                <>
                  <Upload className="h-4 w-4 mr-2" />
                  {value ? "Change Image" : "Select Image"}
                </>
              )}
            </Button>
            <p className="text-xs text-slate-500">Max 5MB. JPEG, PNG, GIF, WEBP</p>
          </div>
        </TabsContent>
        
        <TabsContent value="url" className="mt-0">
          <div className="space-y-3">
            <div className="flex gap-2">
              <Input
                value={value || ""}
                onChange={(e) => onChange(e.target.value)}
                placeholder="https://example.com/image.jpg"
                className="flex-1 bg-white dark:bg-slate-700 border-slate-200 dark:border-slate-600"
                data-testid="image-url-input"
              />
              {value && (
                <Button
                  type="button"
                  size="icon"
                  variant="outline"
                  onClick={handleRemove}
                >
                  <X className="h-4 w-4" />
                </Button>
              )}
            </div>
            
            {value && !value.startsWith("/api") && (
              <div className="relative inline-block">
                <img 
                  src={value}
                  alt="Preview"
                  className="h-32 w-32 object-cover rounded-lg border border-slate-200 dark:border-slate-700"
                  onError={(e) => {
                    e.target.style.display = 'none';
                  }}
                />
              </div>
            )}
          </div>
        </TabsContent>
      </Tabs>
    </div>
  );
}
